/**
 * Shared FP profile controlled-vocabulary options (Feature 074).
 *
 * Frontend source of truth for value -> label mapping of the three structured
 * profile fields. Values mirror the backend Prisma enums exactly; labels are the
 * display strings shown in editors and on the public profile.
 */

export type ClientSegment =
  | 'RETIREES'
  | 'PRE_RETIREES'
  | 'BUSINESS_OWNERS'
  | 'EXECUTIVES'
  | 'ENTREPRENEURS'
  | 'WOMEN_INVESTORS'
  | 'MULTI_GENERATIONAL_FAMILIES'
  | 'FAMILY_CAREGIVERS'
  | 'SOLO_AGERS'
  | 'HIGH_NET_WORTH_FAMILIES'
  | 'ULTRA_HIGH_NET_WORTH_FAMILIES'
  | 'SPECIAL_NEEDS_FAMILIES'
  | 'WIDOWS_AND_WIDOWERS';

export type InvestableAssetsRange =
  | 'UNDER_250K'
  | 'RANGE_250K_1M'
  | 'RANGE_1M_3M'
  | 'RANGE_3M_10M'
  | 'OVER_10M';

export type ServiceOffering =
  | 'FINANCIAL_PLANNING'
  | 'INVESTMENT_MANAGEMENT'
  | 'RETIREMENT_PLANNING'
  | 'TAX_PLANNING_COORDINATION'
  | 'ESTATE_PLANNING_COORDINATION'
  | 'INSURANCE_AND_RISK_MANAGEMENT'
  | 'CASH_FLOW_AND_BUDGET_PLANNING'
  | 'EDUCATION_PLANNING'
  | 'CHARITABLE_GIVING_STRATEGIES'
  | 'BUSINESS_OWNER_PLANNING'
  | 'EXECUTIVE_COMPENSATION_PLANNING'
  | 'STOCK_OPTION_PLANNING'
  | 'ALTERNATIVE_INVESTMENTS'
  | 'FAMILY_AND_LEGACY_PLANNING'
  | 'FAMILY_OFFICE_SERVICES'
  | 'LONGEVITY_AND_LIFE_TRANSITION_SERVICES';

export type FpProfileOption<T extends string> = {
  value: T;
  label: string;
};

export const CLIENT_SEGMENT_OPTIONS: ReadonlyArray<FpProfileOption<ClientSegment>> = [
  { value: 'RETIREES', label: 'Retirees' },
  { value: 'PRE_RETIREES', label: 'Pre-Retirees' },
  { value: 'BUSINESS_OWNERS', label: 'Business Owners' },
  { value: 'EXECUTIVES', label: 'Executives' },
  { value: 'ENTREPRENEURS', label: 'Entrepreneurs' },
  { value: 'WOMEN_INVESTORS', label: 'Women Investors' },
  { value: 'MULTI_GENERATIONAL_FAMILIES', label: 'Multi-Generational Families' },
  { value: 'FAMILY_CAREGIVERS', label: 'Family Caregivers' },
  { value: 'SOLO_AGERS', label: 'Solo Agers' },
  { value: 'HIGH_NET_WORTH_FAMILIES', label: 'High-Net-Worth Families' },
  { value: 'ULTRA_HIGH_NET_WORTH_FAMILIES', label: 'Ultra-High-Net-Worth Families' },
  { value: 'SPECIAL_NEEDS_FAMILIES', label: 'Special Needs Families' },
  { value: 'WIDOWS_AND_WIDOWERS', label: 'Widows & Widowers' },
];

export const INVESTABLE_ASSETS_OPTIONS: ReadonlyArray<FpProfileOption<InvestableAssetsRange>> = [
  { value: 'UNDER_250K', label: '<$250k' },
  { value: 'RANGE_250K_1M', label: '$250k–$1M' },
  { value: 'RANGE_1M_3M', label: '$1M–$3M' },
  { value: 'RANGE_3M_10M', label: '$3M–$10M' },
  { value: 'OVER_10M', label: '$10M+' },
];

export const SERVICE_OFFERING_OPTIONS: ReadonlyArray<FpProfileOption<ServiceOffering>> = [
  { value: 'FINANCIAL_PLANNING', label: 'Financial Planning' },
  { value: 'INVESTMENT_MANAGEMENT', label: 'Investment Management' },
  { value: 'RETIREMENT_PLANNING', label: 'Retirement Planning' },
  { value: 'TAX_PLANNING_COORDINATION', label: 'Tax Planning Coordination' },
  { value: 'ESTATE_PLANNING_COORDINATION', label: 'Estate Planning Coordination' },
  { value: 'INSURANCE_AND_RISK_MANAGEMENT', label: 'Insurance & Risk Management' },
  { value: 'CASH_FLOW_AND_BUDGET_PLANNING', label: 'Cash Flow & Budget Planning' },
  { value: 'EDUCATION_PLANNING', label: 'Education Planning' },
  { value: 'CHARITABLE_GIVING_STRATEGIES', label: 'Charitable Giving Strategies' },
  { value: 'BUSINESS_OWNER_PLANNING', label: 'Business Owner Planning' },
  { value: 'EXECUTIVE_COMPENSATION_PLANNING', label: 'Executive Compensation Planning' },
  { value: 'STOCK_OPTION_PLANNING', label: 'Stock Option Planning' },
  { value: 'ALTERNATIVE_INVESTMENTS', label: 'Alternative Investments' },
  { value: 'FAMILY_AND_LEGACY_PLANNING', label: 'Family & Legacy Planning' },
  { value: 'FAMILY_OFFICE_SERVICES', label: 'Family Office Services' },
  { value: 'LONGEVITY_AND_LIFE_TRANSITION_SERVICES', label: 'Longevity & Life Transition Services' },
];

const buildLabelMap = <T extends string>(
  options: ReadonlyArray<FpProfileOption<T>>,
): Record<string, string> =>
  options.reduce<Record<string, string>>((acc, option) => {
    acc[option.value] = option.label;
    return acc;
  }, {});

const CLIENT_SEGMENT_LABELS = buildLabelMap(CLIENT_SEGMENT_OPTIONS);
const INVESTABLE_ASSETS_LABELS = buildLabelMap(INVESTABLE_ASSETS_OPTIONS);
const SERVICE_OFFERING_LABELS = buildLabelMap(SERVICE_OFFERING_OPTIONS);

export const labelForClientSegment = (value: string): string =>
  CLIENT_SEGMENT_LABELS[value] ?? value;

export const labelForInvestableAssets = (value: string): string =>
  INVESTABLE_ASSETS_LABELS[value] ?? value;

export const labelForServiceOffering = (value: string): string =>
  SERVICE_OFFERING_LABELS[value] ?? value;
